// Brand/offer loading extracted from OfferPageBuilderSimple.js lines 3956-4626
import { useCallback, useEffect } from 'react';
import api from '../../../services/api';
import brandService from '../../../services/brandService';
import { getOffersByBrand } from '../../../services/offerService';
import { ELEMENT_TYPES } from '../../../utils/offerElementTypes';
import { uploadToS3WithProgress } from '../../../services/s3UploadService';

export const useBrands = ({
  selectedBrand,
  setSelectedBrand,
  selectedOffer,
  setSelectedOffer,
  brands,
  setBrands,
  offers,
  setOffers,
  setLoadingBrands,
  setLoadingOffers,
  setSavedOfferElements,
  setFilteredSavedElements,
  templatesOnly,
  templateSelectionOnly,
  setForceRender,
  elements,
  setElements,
  pages,
  setPages,
  selectedElement,
  setSelectedElement,
  setButtonContentTabTempValue,
  setButtonUrlTabTempValue,
  setStatusTitle,
  setStatusMessage,
  setShowStatusAlert,
  setImageUploading,
  setUploadingElementId,
  imageUploading,
  selectedElementContext,
  isBrandsPage,
  paginationEnabled,
  currentPageIndex,
  findAndUpdateLayout,
  updateElement,
}) => {
  // Helper function to auto-populate offer links in elements
  const populateOfferLinkInElements = useCallback((elementsToUpdate, offerUrl) => {
    if (!offerUrl || offerUrl === '#' || offerUrl === '') return elementsToUpdate;

    let anyChanged = false;
    const updatedElements = elementsToUpdate.map(el => {
      let elementChanged = false;
      const newEl = JSON.parse(JSON.stringify(el));

      // 1. Check for buttons array (e.g. VIDEO_WITH_BUTTON_OVERLAY)
      if (newEl.buttons && Array.isArray(newEl.buttons)) {
        newEl.buttons = newEl.buttons.map(btn => {
          if (btn.url !== offerUrl) {
            elementChanged = true;
            return { ...btn, url: offerUrl };
          }
          return btn;
        });
      }

      // 2. Check for button types or types that typically have a URL
      const isButtonType = newEl.type === ELEMENT_TYPES.BUTTON ||
        newEl.type === ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY ||
        newEl.type === 'BUTTON_TEXT_VARIANT' ||
        newEl.type === 'BUTTON_HEADLINE_PRODUCT_IMAGE';

      if ((isButtonType || newEl.url !== undefined) && newEl.url !== offerUrl) {
        newEl.url = offerUrl;
        elementChanged = true;
      }

      // 3. Check for content.buttonUrl (used by some button variants)
      if (newEl.content && typeof newEl.content === 'object' && newEl.content.buttonUrl !== undefined && newEl.content.buttonUrl !== offerUrl) {
        newEl.content.buttonUrl = offerUrl;
        elementChanged = true;
      }

      // 4. Handle nested layouts and image overlays
      if ((newEl.type === ELEMENT_TYPES.LAYOUT || newEl.type === ELEMENT_TYPES.IMAGE_OVERLAY) && newEl.zones) {
        let zonesChanged = false;
        Object.keys(newEl.zones).forEach(zoneId => {
          if (newEl.zones[zoneId].elements) {
            const updatedZoneElements = populateOfferLinkInElements(newEl.zones[zoneId].elements, offerUrl);
            if (updatedZoneElements !== newEl.zones[zoneId].elements) {
              newEl.zones[zoneId].elements = updatedZoneElements;
              zonesChanged = true;
            }
          }
        });
        if (zonesChanged) {
          elementChanged = true;
        }
      }

      if (elementChanged) {
        anyChanged = true;
        return newEl;
      }
      return el;
    });

    return anyChanged ? updatedElements : elementsToUpdate;
  }, []);

  // Auto-populate offer link on offer selection change
  useEffect(() => {
    if (selectedOffer && offers.length > 0 && !templateSelectionOnly) {
      const selectedOfferObj = offers.find(o => o._id === selectedOffer || o.id === selectedOffer);

      if (selectedOfferObj) {
        console.log('🔗 Found selected offer object:', {
          id: selectedOfferObj._id,
          title: selectedOfferObj.title,
          purchaseUrl: selectedOfferObj.purchaseUrl,
          link: selectedOfferObj.link
        });

        const offerUrl = selectedOfferObj.purchaseUrl || selectedOfferObj.link;

        if (offerUrl && offerUrl !== '#' && offerUrl !== '') {
          console.log('🔗 Auto-populating offer link from useEffect:', offerUrl);

          // 1. Update current elements on canvas
          setElements(prev => {
            const updated = populateOfferLinkInElements(prev, offerUrl);
            if (updated !== prev) {
              console.log('✅ Elements updated with offer link');
              return updated;
            }
            return prev;
          });

          // 2. Update all pages if pagination is active
          if (pages.length > 0) {
            setPages(prevPages => {
              let pagesChanged = false;
              const updatedPages = prevPages.map(page => {
                const updatedElements = populateOfferLinkInElements(page.elements || [], offerUrl);
                if (updatedElements !== page.elements) {
                  pagesChanged = true;
                  return { ...page, elements: updatedElements };
                }
                return page;
              });
              return pagesChanged ? updatedPages : prevPages;
            });
          }

          // 3. Sync selectedElement
          if (selectedElement) {
            const updatedArr = populateOfferLinkInElements([selectedElement], offerUrl);
            if (updatedArr[0] !== selectedElement) {
              console.log('✅ Syncing selectedElement with auto-populated offer link');
              setSelectedElement(updatedArr[0]);
              // Clear temp values so the UI uses updated selectedElement values
              setButtonContentTabTempValue('');
              setButtonUrlTabTempValue('');
            }
          }
        } else {
          console.warn('⚠️ Selected offer has no valid purchaseUrl or link:', selectedOfferObj);
        }
      } else {
        console.warn('⚠️ Could not find selected offer in offers list:', selectedOffer);
      }
    }
  }, [selectedOffer, offers, templateSelectionOnly, setElements, setPages, setSelectedElement, selectedElement?.id, setButtonContentTabTempValue, setButtonUrlTabTempValue, populateOfferLinkInElements]);

  // Load brands from database
  const loadBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await brandService.getAllBrands();
      setBrands(response.brands || []);
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoadingBrands(false);
    }
  };

  // Load offers for selected brand
  const loadOffersForBrand = async (brandId) => {
    if (!brandId) {
      setOffers([]);
      return;
    }

    try {
      setLoadingOffers(true);
      const response = await getOffersByBrand(brandId, 1, 100, true); // Include expired offers
      setOffers(response.offers || []);
    } catch (error) {
      console.error('Error loading offers for brand:', error);
      setOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  };

  // Auto-load offers when selectedBrand changes (e.g. from props)
  useEffect(() => {
    if (selectedBrand) {
      console.log('🏷️ Automatically loading offers for brand:', selectedBrand);
      loadOffersForBrand(selectedBrand);
    } else {
      setOffers([]);
    }
  }, [selectedBrand]);

  // Load saved items only when both brand and offer are selected
  useEffect(() => {
    console.log('🔄 Brand/Offer selection changed:', {
      selectedBrand,
      selectedOffer,
      templatesOnly
    });

    if (selectedBrand && selectedOffer) {
      console.log('🎯 Loading saved items for brand:', selectedBrand, 'offer:', selectedOffer, 'templatesOnly:', templatesOnly);

      const loadItems = async () => {
        try {
          const params = {};

          if (selectedBrand && selectedBrand !== '') {
            params.brandId = selectedBrand;
          }

          if (selectedOffer && selectedOffer !== '') {
            params.offerId = selectedOffer;
          }

          console.log('📤 API request params:', params);
          const response = await api.get('/offer-elements', { params });
          console.log('✅ API response:', response.data);

          // Use API response directly - no filtering needed
          setFilteredSavedElements(response.data);
          setSavedOfferElements(response.data);

          console.log('✅ Set saved elements to:', response.data.length, 'items from API');

          // Force a re-render to ensure UI updates
          setForceRender(prev => prev + 1);
        } catch (error) {
          console.error('❌ Error loading saved items:', error);
          setFilteredSavedElements([]);
          setSavedOfferElements([]);
        }
      };

      loadItems();
    } else {
      console.log('⏸️ Skipping API call - brand or offer not selected');
      // Clear saved elements when conditions are not met
      setFilteredSavedElements([]);
      setSavedOfferElements([]);
    }
  }, [selectedBrand, selectedOffer, templatesOnly]);

  // Handle brand selection
  const handleBrandChange = (brandId) => {
    console.log('🏷️ Brand selection changed to:', brandId);
    console.log('🏷️ Previous brand was:', selectedBrand);
    setSelectedBrand(brandId);
    setSelectedOffer(''); // Reset offer when brand changes
    if (brandId) {
      loadOffersForBrand(brandId);
    } else {
      setOffers([]);
    }
  };

  // Video upload function
  const handleVideoUpload = useCallback(async (file, elementId, zoneContext = null) => {
    if (!file) return null;

    try {
      setImageUploading(true);
      setUploadingElementId(elementId);

      const formData = new FormData();
      formData.append('files', file);

      const response = await api.post('/offer-templates/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.attachments && response.data.attachments.length > 0) {
        const videoUrl = response.data.attachments[0].fileUrl;

        return videoUrl;
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload video. Please try again.';
      setStatusTitle('Upload Failed');
      setStatusMessage(errorMessage);
      setShowStatusAlert(true);
    } finally {
      setImageUploading(false);
      setUploadingElementId(null);
    }

    return null;
  }, []);

  // Thumbnail upload function - using same approach as BlogsPage
  const handleThumbnailUpload = useCallback(async (file, elementId, zoneContext = null) => {
    if (!file) return null;

    try {
      setImageUploading(true);
      setUploadingElementId(elementId);

      const uploadedUrl = await uploadToS3WithProgress(file, 'offer-templates/thumbnails', (progress) => {
        console.log('Thumbnail upload progress:', progress);
      });

      return uploadedUrl;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload thumbnail. Please try again.';
      setStatusTitle('Upload Failed');
      setStatusMessage(errorMessage);
      setShowStatusAlert(true);
    } finally {
      setImageUploading(false);
      setUploadingElementId(null);
    }

    return null;
  }, []);

  // Image upload function
  const handleImageUpload = useCallback(async (file, elementId, isForCallout = false, zoneContext = null, fieldName = 'content') => {
    if (!file) return null;

    try {
      setImageUploading(true);
      setUploadingElementId(elementId);

      const formData = new FormData();
      formData.append('files', file);

      const response = await api.post('/offer-templates/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.attachments && response.data.attachments.length > 0) {
        const imageUrl = response.data.attachments[0].fileUrl;

        // Update the element with the uploaded image URL
        if (!isForCallout) {
          // Use zone context if provided, otherwise use selectedElementContext
          const context = zoneContext || selectedElementContext;
          updateElement(
            elementId,
            { [fieldName]: imageUrl },
            context?.isInZone,
            context?.layoutId,
            context?.zoneId
          );

          // Force immediate re-render for image updates
          console.log('🔄 Forcing immediate re-render after image upload');
          setForceRender(prev => prev + 1);
        }

        return imageUrl;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload image. Please try again.';
      setStatusTitle('Upload Failed');
      setStatusMessage(errorMessage);
      setShowStatusAlert(true);
    } finally {
      setImageUploading(false);
      setUploadingElementId(null);
    }

    return null;
  }, [selectedElementContext]);

  // Media upload function - similar to blog creation flow
  const handleMediaUploadForElement = useCallback(async (file, elementId, zoneContext = null) => {
    if (!file) return null;

    try {
      setImageUploading(true);
      setUploadingElementId(elementId);

      // Determine folder based on file type
      const isVideo = file.type.startsWith('video/');
      const folder = isVideo ? 'offer-templates/videos' : 'offer-templates/images';

      // Upload to S3
      const uploadedUrl = await uploadToS3WithProgress(file, folder, (progress) => {
        console.log('Media upload progress:', progress);
      });

      const context = zoneContext || selectedElementContext;

      // In brands page mode, if it's an image (not video), convert MEDIA element to FITTABLE_IMAGE
      if (isBrandsPage && !isVideo) {
        // Helper function to find element by ID in elements array or nested layouts
        const findElementById = (elementsArray, targetId) => {
          for (const el of elementsArray) {
            if (el.id === targetId) {
              return { element: el, inZone: false };
            }
            // Check nested layouts and image overlays
            if ((el.type === ELEMENT_TYPES.LAYOUT || el.type === ELEMENT_TYPES.IMAGE_OVERLAY) && el.zones) {
              for (const zone of el.zones) {
                const found = findElementById(zone.elements || [], targetId);
                if (found) {
                  return { ...found, layoutId: el.id, zoneId: zone.id, inZone: true };
                }
              }
            }
          }
          return null;
        };

        // Try to find element in current elements array
        let elementInfo = findElementById(elements, elementId);

        // If not found, try in pages
        if (!elementInfo && paginationEnabled) {
          for (const page of pages) {
            elementInfo = findElementById(page.elements || [], elementId);
            if (elementInfo) break;
          }
        }

        if (elementInfo && elementInfo.element && elementInfo.element.type === ELEMENT_TYPES.MEDIA) {
          // Convert to FITTABLE_IMAGE with the uploaded image
          const fittableImageElement = {
            ...elementInfo.element,
            type: ELEMENT_TYPES.FITTABLE_IMAGE,
            imageSrc: uploadedUrl,
            imagePosition: { x: 0, y: 0 },
            imageScale: 1,
            mediaUrl: undefined, // Remove mediaUrl
            thumbnailUrl: undefined // Remove thumbnailUrl
          };

          // Update element using the updateElement function which handles zones
          if (elementInfo.inZone && elementInfo.layoutId && elementInfo.zoneId) {
            // Element is in a zone, use findAndUpdateLayout
            if (paginationEnabled) {
              setPages(prevPages => {
                return prevPages.map(page => ({
                  ...page,
                  elements: findAndUpdateLayout(page.elements, elementInfo.layoutId, elementInfo.zoneId, (zone) => ({
                    ...zone,
                    elements: zone.elements.map(el =>
                      el.id === elementId ? fittableImageElement : el
                    )
                  }))
                }));
              });
            }

            setElements(prev => {
              return findAndUpdateLayout(prev, elementInfo.layoutId, elementInfo.zoneId, (zone) => ({
                ...zone,
                elements: zone.elements.map(el =>
                  el.id === elementId ? fittableImageElement : el
                )
              }));
            });
          } else {
            // Element is in main canvas
            if (paginationEnabled) {
              setPages(prevPages => {
                return prevPages.map(page => ({
                  ...page,
                  elements: page.elements.map(el =>
                    el.id === elementId ? fittableImageElement : el
                  )
                }));
              });
            }

            setElements(prev => prev.map(el =>
              el.id === elementId ? fittableImageElement : el
            ));
          }

          // Update selected element
          setSelectedElement(fittableImageElement);

          setForceRender(prev => prev + 1);
          return uploadedUrl;
        }
      }

      // For videos or non-brands page, update element with media URL normally
      updateElement(
        elementId,
        { mediaUrl: uploadedUrl },
        context?.isInZone,
        context?.layoutId,
        context?.zoneId
      );

      // Also update selectedElement directly for immediate UI update
      setSelectedElement(prev => {
        if (prev?.id === elementId) {
          return { ...prev, mediaUrl: uploadedUrl };
        }
        return prev;
      });

      setForceRender(prev => prev + 1);

      return uploadedUrl;
    } catch (error) {
      console.error('Error uploading media:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload media. Please try again.';
      setStatusTitle('Upload Failed');
      setStatusMessage(errorMessage);
      setShowStatusAlert(true);
    } finally {
      setImageUploading(false);
      setUploadingElementId(null);
    }

    return null;
  }, [selectedElementContext, updateElement, isBrandsPage, elements, pages, currentPageIndex, paginationEnabled, setPages, setElements, findAndUpdateLayout]);

  // Load brands on component mount (saved elements will be loaded by filter effect)
  useEffect(() => {
    loadBrands();
  }, []);

  return {
    populateOfferLinkInElements,
    loadBrands,
    loadOffersForBrand,
    handleBrandChange,
    handleVideoUpload,
    handleThumbnailUpload,
    handleImageUpload,
    handleMediaUploadForElement,
  };
};
